// Apolo IDE — PlanPreview (Sprint v4 Epic S2 WS S2.3).
//
// Renders the structured plan returned by /api/agents/decompose. Each
// SubagentSpec collapses/expands so Pedro can scan first and drill into
// the tools + cost ceiling per subagent. The component is read-only —
// approve/reject actions live in PlanApprovalActions so the two can be
// composed independently (e.g. embedded in a modal vs the side panel).

import { useState } from 'react';
import { ChevronDown, ChevronRight, Cpu, AlertTriangle, Wrench } from 'lucide-react';

function fmtUsd(c) {
  if (typeof c !== 'number' || !Number.isFinite(c)) return '$0';
  if (c < 0.001) return '< $0.001';
  return `$${c.toFixed(c < 0.01 ? 4 : 3)}`;
}

function fmtTokens(n) {
  if (typeof n !== 'number') return '0';
  if (n < 1000) return String(n);
  return `${(n / 1000).toFixed(1)}k`;
}

function mcpBadgeClass(level) {
  if (level === 'read-write') return 'bg-apolo-warning/15 text-apolo-warning';
  if (level === 'read') return 'bg-apolo-accent/15 text-apolo-accent';
  return 'bg-apolo-surface2 text-apolo-textTertiary';
}

function SubagentCard({ spec, index }) {
  const [open, setOpen] = useState(false);
  return (
    <article
      data-testid="plan-subagent-card"
      data-index={index}
      className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface1"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`plan-subagent-body-${index}`}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <div className="flex min-w-0 items-center gap-2">
          {open ? (
            <ChevronDown size={14} className="text-apolo-textTertiary" aria-hidden="true" />
          ) : (
            <ChevronRight size={14} className="text-apolo-textTertiary" aria-hidden="true" />
          )}
          <span className="text-[11px] font-mono text-apolo-textTertiary">#{index + 1}</span>
          <span className="truncate text-xs text-apolo-textPrimary">{spec.goal}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-[11px]">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${mcpBadgeClass(spec.mcpAccess)}`}
            aria-label={`MCP access ${spec.mcpAccess}`}
          >
            {spec.mcpAccess}
          </span>
          <span className="font-mono text-apolo-textSecondary">{spec.model}</span>
        </div>
      </button>

      {open ? (
        <div
          id={`plan-subagent-body-${index}`}
          className="border-t border-apolo-borderSubtle px-3 py-2 text-[11px] text-apolo-textSecondary"
        >
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
            <dt className="text-apolo-textTertiary">Tokens (cap)</dt>
            <dd className="font-mono text-apolo-textPrimary">{fmtTokens(spec.maxTokens)}</dd>
            <dt className="text-apolo-textTertiary">Cost (cap)</dt>
            <dd className="font-mono text-apolo-textPrimary">{fmtUsd(spec.maxCostUsd)}</dd>
            <dt className="text-apolo-textTertiary">Time (cap)</dt>
            <dd className="font-mono text-apolo-textPrimary">{Math.round(spec.maxTimeMs / 1000)}s</dd>
            <dt className="text-apolo-textTertiary">Tools</dt>
            <dd className="font-mono text-apolo-textPrimary">
              {spec.tools && spec.tools.length > 0 ? (
                <span className="inline-flex flex-wrap items-center gap-1">
                  <Wrench size={9} aria-hidden="true" />
                  {spec.tools.join(', ')}
                </span>
              ) : (
                <span className="text-apolo-textTertiary">none</span>
              )}
            </dd>
          </dl>
        </div>
      ) : null}
    </article>
  );
}

export default function PlanPreview({ plan, budget }) {
  if (!plan) {
    return (
      <div
        data-testid="plan-preview-empty"
        className="rounded-lg border border-dashed border-apolo-borderSubtle bg-apolo-surface1 p-4 text-center text-xs text-apolo-textTertiary"
      >
        Nenhum plano gerado ainda.
      </div>
    );
  }
  const subs = Array.isArray(plan.subagents) ? plan.subagents : [];
  const total = typeof plan.estimatedCostUsd === 'number' ? plan.estimatedCostUsd : 0;

  return (
    <section data-testid="plan-preview" aria-label="Plano gerado pelo Decomposer" className="space-y-3">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-medium text-apolo-textPrimary">
            <Cpu size={12} className="text-apolo-accent" aria-hidden="true" />
            <span>{plan.summary || 'Plano gerado'}</span>
          </div>
          {plan.reasoning ? (
            <p className="mt-1 text-[11px] text-apolo-textTertiary" data-testid="plan-reasoning">
              {plan.reasoning}
            </p>
          ) : null}
        </div>
        <div className="shrink-0 text-right text-[11px]">
          <div className="text-apolo-textTertiary">Subagents</div>
          <div className="font-mono text-apolo-textPrimary">{subs.length}</div>
        </div>
      </header>

      <div className="space-y-2" data-testid="plan-subagent-list">
        {subs.map((s, i) => (
          <SubagentCard key={s.id || i} spec={s} index={i} />
        ))}
      </div>

      <footer className="flex items-center justify-between gap-2 border-t border-apolo-borderSubtle pt-2 text-[11px]">
        <span className="text-apolo-textTertiary">
          Cost estimate: <span className="font-mono text-apolo-textPrimary">{fmtUsd(total)}</span>
        </span>
        {budget && budget.warning ? (
          <span
            data-testid="plan-budget-warning"
            className="inline-flex items-center gap-1 rounded-full bg-apolo-warning/15 px-2 py-0.5 text-apolo-warning"
          >
            <AlertTriangle size={10} aria-hidden="true" />
            ${(budget.projectedSpend || 0).toFixed(2)} / ${(budget.monthlyCap || 0).toFixed(0)}
          </span>
        ) : null}
        {budget && budget.allowed === false ? (
          <span
            data-testid="plan-budget-blocked"
            className="inline-flex items-center gap-1 rounded-full bg-apolo-error/15 px-2 py-0.5 text-apolo-error"
          >
            <AlertTriangle size={10} aria-hidden="true" />
            Budget exceeded
          </span>
        ) : null}
      </footer>
    </section>
  );
}
